import React, { useRef, useState } from 'react';
import cn from 'classnames';

import useKeypress from '@/hooks/useKeypress.hook';
import { SearchTagTypeEnum } from '@/graphql/contracts/_generated_/contracts';
import useCreateSearchTag from '@/pages/Home/hooks/useCreateSearchTag';
import styles from './main-search.module.scss';

export type SearchedContract = {
  contractID: string;
  contractNumber: string;
  __typename: string;
};

export type SearchedCustomer = {
  customerAccountNumber: string;
  customerID: string;
  customerName: string;
};

export type SearchItemCombined = SearchedContract | SearchedCustomer;

export interface IMainSearchResultListItem {
  id: string,
  title: string | JSX.Element,
  callbackData: any
}

export interface IMainSearchResultList {
  title: string,
  handleClick: (res: any) => void,
  items: IMainSearchResultListItem[]
}

interface IMainSearchResultProps {
  data: IMainSearchResultList[],
  blurInput: () => void
}

export default function MainSearchResult({ data, blurInput }: IMainSearchResultProps): JSX.Element {
  const [focusedItemIndex, setFocusedItemIndex] = useState<number>(0);
  let { current: listItemRefs } = useRef<HTMLButtonElement[]>([]);
  let { current: itemIndex } = useRef<number>(0);
  const { createSearchTag } = useCreateSearchTag();

  const handleArrowDown = () => {
    if ((focusedItemIndex + 1) > itemIndex) {
      setFocusedItemIndex(1);
    } else {
      setFocusedItemIndex((prevState) => prevState + 1);
    }
  };

  const handleArrowUp = () => {
    if ((focusedItemIndex - 1) <= 0) {
      setFocusedItemIndex(itemIndex);
    } else {
      setFocusedItemIndex((prevState) => prevState - 1);
    }
  };

  const handleEnter = () => {
    if (focusedItemIndex && listItemRefs.length) {
      listItemRefs[focusedItemIndex - 1].click();
    }
  };

  const handleSaveSearchTag = (searchItem: SearchItemCombined) => {
    const tagType = 'customerID' in searchItem ? SearchTagTypeEnum.CUSTOMER : SearchTagTypeEnum.CONTRACT;
    const tag = 'customerID' in searchItem ? searchItem.customerName : searchItem.contractNumber;
    const targetID = 'customerID' in searchItem ? searchItem.customerID : searchItem.contractID;

    createSearchTag({
      tag: tag,
      tagType: tagType,
      targetID: targetID
    });

  };

  useKeypress('ArrowDown', handleArrowDown);
  useKeypress('ArrowUp', handleArrowUp);
  useKeypress('Enter', handleEnter);

  const renderList = () => {
    itemIndex = 0;
    listItemRefs = [];

    const jsx = data?.map((list: any) => (
      <div className={styles['result-list']} key={list.title}>
        <div className={styles['result-section-title']}>{list.title}</div>

        {list?.items.map((item: IMainSearchResultListItem) => {
          itemIndex++;

          return (
            <button
              type="button"
              ref={(el) => {
                if (el) {
                  listItemRefs.push(el);
                }
              }}
              key={item.id}
              tabIndex={itemIndex}
              onClick={() => {
                list.handleClick(item.callbackData);
                handleSaveSearchTag(item.callbackData);
                blurInput();
              }}
              className={cn(styles['result-list-item'], {
                [styles.focused]: itemIndex === focusedItemIndex,
              })}
            >
              {item.title}
            </button>
          );
        })}
      </div>
    ));

    return jsx;
  };

  return (
    <div
      className={cn(styles['search-result'], {
        [styles.empty]: !data.length,
      })}
    >
      {renderList()}
    </div>
  );
}
